import { formatDate } from '@angular/common';
import { Injectable, inject } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { cloneDeep, isEmpty } from 'lodash';
import { catchError, concatMap, delay, EMPTY, exhaustMap, filter, iif, map, of, scan, switchMap, tap } from 'rxjs';

import { DetailType } from '@ninety/platform/models/_shared/detail-type.enum';
import { OrdinalOrUserOrdinalUpdate } from '@ninety/platform/models/_shared/ordinal-or-user-ordinal-update';
import { ItemType } from '@ninety/platform/models/enums/item-type';
import type { HeadlinesSortField } from '@ninety/platform/models/headlines/headline-sort';
import { FromLinkedItem, LinkedItemTypeEnum } from '@ninety/platform/models/linked-items/linked-item-type-enum';
import { Headline, HeadlineReceivedRealtimeMessage } from '@ninety/platform/models/meetings/headline';
import { HeadlineMessageType } from '@ninety/platform/models/meetings/headline-message-type';
import { ListSortMessage } from '@ninety/platform/models/meetings/list-sort-message';
import {
  ExcelExportType,
  FilterService,
  FilterServiceActions,
  LocalStorageService,
  extractValueFromStore,
} from '@ninety/platform/services';
import {
  DetailViewActions,
  MazSharedActions,
  MazToolName,
  NotificationActions,
  SpinnerActions,
  appActions,
  RealTimeActions,
  TeamSelectors,
  FilterUserSelectors,
  selectCurrentUser,
  selectCurrentUserId,
  selectLanguage,
} from '@ninety/platform/state';
import { ConfirmDialogComponent, ConfirmDialogData, WarningConfirmDialogComponent } from '@ninety/ui/core';
import { FileService } from '@ninety/ui/legacy/core/services/file.service';
import { PrintApi, PrintService } from '@ninety/ui/legacy/core/services/print.service';
import { CreateDialogService } from '@ninety/web/pages/layouts/services/create-dialog.service';
import { MeetingService } from '@ninety/web/pages/meeting/_shared/services/meeting.service';
import { MeetingsStateSelectors } from '@ninety/web/pages/meeting/_state/meetings.selectors';

import { HeadlineService } from '../../_shared/services/headline.service';
import { CascadingMessagesStateActions } from '../cascading-messages/cascading-messages-state.actions';
import * as cascadingMessagesSelectors from '../cascading-messages/cascading-messages-state.selectors';

import { HeadlinesStateActions } from './headlines-state.actions';
import { HeadlineStateKey } from './headlines-state.model';
import * as headlinesSelectors from './headlines-state.selectors';

@Injectable()
export class HeadlinesStateEffects {
  private actions$ = inject(Actions);
  private headlineService = inject(HeadlineService);
  private localStorageService = inject(LocalStorageService);
  private store = inject(Store);
  private createDialogService = inject(CreateDialogService);
  private meetingService = inject(MeetingService);
  private printService = inject(PrintService);
  private filterService = inject(FilterService);
  private fileService = inject(FileService);
  private dialog = inject(MatDialog);

  static readonly source = 'HeadlinesStateEffects';

  paginationChange$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(HeadlinesStateActions.paginationChange),
        tap(({ size }) => this.localStorageService.set(`${HeadlineStateKey}.pagination.size`, size))
      ),
    { dispatch: false }
  );

  sortChange$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(HeadlinesStateActions.sortChange),
        tap(({ sort }) => {
          if (sort.direction) {
            //direction is null when sort cycle is complete
            this.localStorageService.set(`${HeadlineStateKey}.sort`, sort);
          } else {
            this.localStorageService.delete(`${HeadlineStateKey}.sort`);
          }
        })
      ),
    { dispatch: false }
  );

  clearSort$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(HeadlinesStateActions.clearSort),
        tap(_ => this.localStorageService.delete(`${HeadlineStateKey}.sort`))
      ),
    { dispatch: false }
  );

  broadcastOnSortChange$ = createEffect(() =>
    this.actions$.pipe(
      ofType(HeadlinesStateActions.sortChange),
      filter(_ => this.headlineService.shouldBroadcast),
      concatLatestFrom(() => [
        this.store.select(headlinesSelectors.selectFilterByTeamId),
        this.store.select(MeetingsStateSelectors.selectCurrentMeetingPresenterUserId),
        this.store.select(selectCurrentUser),
      ]),
      filter(([, , currentMeetingPresenterUserId, currentUser]) => {
        // Only send broadcast messages if the current user is the presenter
        // We don't want to send messages if the user is not presenter
        return !currentUser?.isObserver && currentMeetingPresenterUserId === currentUser._id;
      }),
      map(([{ sort }, teamId]) =>
        HeadlinesStateActions.broadcastMessage({
          message: {
            messageType: HeadlineMessageType.sort,
            document: {
              listType: 'headlines',
              field: sort.field as string,
              currentTeamId: teamId,
              sortDirection: sort.direction,
              screen: 'headlines',
              //TODO NEXT: handle conclude when refactored. maybe even skip this for conclude as lists are combined,
              //because lists are combined on conclude and we save sort in local storage
            },
          },
        })
      )
    )
  );

  openCreateDialog$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(HeadlinesStateActions.openCreateDialog),
        switchMap(() => this.createDialogService.open({ itemType: ItemType.headline }))
      ),
    { dispatch: false }
  );

  refreshHeadlineListsOnMazToolUse$ = createEffect(() =>
    this.actions$.pipe(
      ofType(MazSharedActions.toolUse),
      filter(({ toolName }) => toolName === MazToolName.createHeadline || toolName === MazToolName.editHeadline),
      concatLatestFrom(() => this.store.select(headlinesSelectors.selectIsHeadlinesListPage)),
      filter(([, isHeadlinesListPage]) => isHeadlinesListPage),
      delay(3000),
      switchMap(() => of(HeadlinesStateActions.getInBackground(), CascadingMessagesStateActions.getInBackground()))
    )
  );

  get$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        HeadlinesStateActions.get,
        HeadlinesStateActions.getInBackground,
        HeadlinesStateActions.filterByTeam,
        HeadlinesStateActions.sortChange,
        HeadlinesStateActions.paginationChange,
        HeadlinesStateActions.showArchived,
        HeadlinesStateActions.search,
        HeadlinesStateActions.archiveAllCompletedSuccess
      ),
      concatLatestFrom(() => [this.store.select(headlinesSelectors.selectGetHeadlineQueryParams)]),
      switchMap(([_, params]) =>
        this.headlineService.getHeadlinesByTeamId(params).pipe(
          map(headlines =>
            HeadlinesStateActions.getSuccess({ headlines, meeting: cloneDeep(this.meetingService.currentMeeting) })
          ),
          catchError((error: unknown) => of(HeadlinesStateActions.getFailed({ error })))
        )
      )
    )
  );

  setSelectedHeadline$ = createEffect(() =>
    this.actions$.pipe(
      ofType(HeadlinesStateActions.getSuccess),
      concatLatestFrom(() => [this.store.select(headlinesSelectors.selectHeadlineIdFromDetailRoute)]),
      filter(([_, headlineId]) => !!headlineId),
      map(([_, headlineId]) => HeadlinesStateActions.setSelected({ selectedId: headlineId }))
    )
  );

  setSelectedHeadlineOnDetailOpen$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DetailViewActions.opened),
      filter(({ config }) => config.pathParts[0] === DetailType.headline),
      map(({ config }) => HeadlinesStateActions.setSelected({ selectedId: config.pathParts[1] }))
    )
  );

  openInDetailView$ = createEffect(() =>
    this.actions$.pipe(
      ofType(HeadlinesStateActions.openInDetailView),
      map(action =>
        DetailViewActions.opened({
          config: { pathParts: [DetailType.headline, action.headlineId] },
          source: 'Headline Selected',
        })
      )
    )
  );

  clearSelectedOnClosed$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DetailViewActions.closed),
      filter(({ itemType }) => this.isHeadlineAction(itemType)),
      map(() => HeadlinesStateActions.clearSelected())
    )
  );

  saveInline$ = createEffect(() =>
    this.actions$.pipe(
      ofType(HeadlinesStateActions.saveInline),
      concatMap(({ headline }) => {
        const { _id: _, ...noIdHeadline } = headline;
        return this.headlineService.createHeadline(noIdHeadline).pipe(
          //NOTE: saving inline should create only one headline so we can do [0]
          map(response => HeadlinesStateActions.saveInlineSuccess({ headline: response[0] })),
          catchError((error: unknown) => of(HeadlinesStateActions.saveInlineFailed({ error })))
        );
      })
    )
  );

  onSave$ = createEffect(() =>
    this.actions$.pipe(
      ofType(HeadlinesStateActions.saveInlineSuccess, HeadlinesStateActions.saveInlineFailed),
      map(_ => HeadlinesStateActions.cancelAddInline())
    )
  );

  broadcastOnCreateHeadline$ = createEffect(() =>
    this.actions$.pipe(
      ofType(HeadlinesStateActions.add),
      filter(_ => this.headlineService.shouldBroadcast),
      map(({ headline }) =>
        HeadlinesStateActions.broadcastMessage({
          message: {
            messageType: HeadlineMessageType.new,
            document: headline._id,
            isCascadingMessage: false,
          },
        })
      )
    )
  );

  update$ = createEffect(() =>
    this.actions$.pipe(
      ofType(HeadlinesStateActions.update),
      concatMap(({ update }) =>
        this.headlineService.updateHeadline(update.id as string, update.changes).pipe(
          map(_ => HeadlinesStateActions.updateSuccess({ update })),
          catchError((error: unknown) => of(HeadlinesStateActions.updateFailed({ error })))
        )
      )
    )
  );

  //TODO NEXT: remove/refactor when meeting is moved to store
  updateDoneHeadlinesDocsInMeeting$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(HeadlinesStateActions.updateSuccess, HeadlinesStateActions.setUserSuccess),
        filter(_ => this.meetingService.currentMeeting?.inProgress),
        tap(({ update }) => {
          const doneHeadlineDocs = [...(this.meetingService.currentMeeting.doneHeadlineDocs || [])];
          const indexH = doneHeadlineDocs.findIndex(h => h._id === update.id);
          if (indexH >= 0) {
            doneHeadlineDocs[indexH] = { ...doneHeadlineDocs[indexH], ...update.changes };
            this.meetingService.setCurrentMeeting({ ...this.meetingService.currentMeeting, doneHeadlineDocs });
          }

          const cascadingMessageDocs = [...(this.meetingService.currentMeeting.cascadingMessageDocs || [])];
          const indexCm = cascadingMessageDocs.findIndex(h => h._id === update.id);
          if (indexCm >= 0) {
            cascadingMessageDocs[indexCm] = { ...cascadingMessageDocs[indexCm], ...update.changes };
            this.meetingService.setCurrentMeeting({ ...this.meetingService.currentMeeting, cascadingMessageDocs });
          }
        })
      ),
    { dispatch: false }
  );

  broadcastOnUpdateSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(HeadlinesStateActions.updateSuccess),
      filter(_ => this.headlineService.shouldBroadcast),
      map(({ update }) =>
        HeadlinesStateActions.broadcastMessage({
          message: {
            messageType: HeadlineMessageType.headline,
            document: { ...update.changes, _id: update.id as string },
            isCascadingMessage: false,
          },
        })
      )
    )
  );

  setTeam$ = createEffect(() =>
    this.actions$.pipe(
      ofType(HeadlinesStateActions.setTeam),
      concatMap(({ id, teamId }) =>
        this.headlineService.updateHeadline(id, { teamId }).pipe(
          map(_ => HeadlinesStateActions.setTeamSuccess({ id, teamId })),
          catchError((error: unknown) => of(HeadlinesStateActions.setTeamFailed({ error })))
        )
      )
    )
  );

  broadcastOnSetTeamSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(HeadlinesStateActions.setTeamSuccess),
      filter(_ => this.headlineService.shouldBroadcast),
      map(({ id, teamId }) =>
        HeadlinesStateActions.broadcastMessage({
          message: {
            messageType: HeadlineMessageType.headline,
            document: { teamId, _id: id },
            isCascadingMessage: false,
          },
        })
      )
    )
  );

  setUser$ = createEffect(() =>
    this.actions$.pipe(
      ofType(HeadlinesStateActions.setUser),
      concatMap(({ id, ownedByUserId }) =>
        this.headlineService.updateHeadline(id, { ownedByUserId }).pipe(
          map(update =>
            HeadlinesStateActions.setUserSuccess({
              update: { id, changes: { ownedByUserId: update.ownedByUserId, ownedByUser: update.ownedByUser } },
            })
          ),
          catchError((error: unknown) => of(HeadlinesStateActions.setUserFailed({ error })))
        )
      )
    )
  );

  broadcastOnSetUserSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(HeadlinesStateActions.setUserSuccess),
      filter(_ => this.headlineService.shouldBroadcast),
      map(({ update }) =>
        HeadlinesStateActions.broadcastMessage({
          message: {
            messageType: HeadlineMessageType.headline,
            document: { ...update.changes, _id: update.id as string },
            isCascadingMessage: false,
          },
        })
      )
    )
  );

  setArchived$ = createEffect(() =>
    this.actions$.pipe(
      ofType(HeadlinesStateActions.setArchived),
      concatMap(({ id, archived }) =>
        this.headlineService.setArchived(id, archived).pipe(
          map(_ => HeadlinesStateActions.setArchivedSuccess({ id, archived })),
          catchError((error: unknown) => of(HeadlinesStateActions.setArchivedFailed({ error })))
        )
      )
    )
  );

  broadcastSetArchivedSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(HeadlinesStateActions.setArchivedSuccess),
      filter(_ => this.headlineService.shouldBroadcast),
      map(({ id, archived }) =>
        HeadlinesStateActions.broadcastMessage({
          message: {
            messageType: archived ? HeadlineMessageType.archive : HeadlineMessageType.unarchive,
            document: id,
            isCascadingMessage: false,
          },
        })
      )
    )
  );

  setCompleted$ = createEffect(() =>
    this.actions$.pipe(
      ofType(HeadlinesStateActions.setCompleted),
      concatMap(({ id, completed }) =>
        this.headlineService.setCompleted(id, completed, this.meetingService.currentMeeting?._id).pipe(
          map(update => {
            if (!update.isDone) {
              //remove isDone meta fields by setting them to undefined,
              update.completedDate = undefined;
              update.completedBy = undefined;
              update.completedInMeeting = undefined;
            }
            return HeadlinesStateActions.setCompletedSuccess({ update: { id, changes: update } });
          }),
          catchError((error: unknown) => of(HeadlinesStateActions.setCompletedFailed({ error })))
        )
      )
    )
  );

  setCompletedSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(HeadlinesStateActions.setCompletedSuccess),
      filter(_ => this.meetingService.currentMeeting?.inProgress),
      concatMap(({ update }) =>
        iif(
          () => update.changes.isDone,
          this.meetingService.addDoneHeadline(update.id as string).pipe(map(_ => update)),
          this.meetingService.removeDoneHeadline(update.id as string).pipe(map(_ => update))
        ).pipe(
          map(update => HeadlinesStateActions.setCompletedInMeetingSuccess({ update })),
          catchError((error: unknown) => of(HeadlinesStateActions.setCompletedInMeetingFailed({ error })))
        )
      )
    )
  );

  broadcastSetCompletedInMeeting$ = createEffect(() =>
    this.actions$.pipe(
      ofType(HeadlinesStateActions.setCompletedInMeetingSuccess),
      filter(_ => this.headlineService.shouldBroadcast),
      map(({ update }) =>
        HeadlinesStateActions.broadcastMessage({
          message: {
            messageType: HeadlineMessageType.headline,
            document: { ...update.changes, _id: update.id as string },
            isCascadingMessage: false,
          },
        })
      )
    )
  );

  deleteConfirm$ = createEffect(() =>
    this.actions$.pipe(
      ofType(HeadlinesStateActions.deleteConfirm),
      concatLatestFrom(() => this.store.select(selectLanguage)),
      exhaustMap(([{ id }, language]) => {
        const itemLabel = language?.headline?.item ?? 'Headline';
        return this.dialog
          .open<ConfirmDialogComponent, ConfirmDialogData>(ConfirmDialogComponent, {
            data: ConfirmDialogData.WarningDialog({
              title: `Delete ${itemLabel}`,
              message: `Are you sure you want to delete this ${itemLabel}?`,
              additionalMessage: `This will move the ${itemLabel} to the trash. Items in the trash will be permanently deleted after 90 days.`,
            }),
          })
          .afterClosed()
          .pipe(map((confirmed: boolean) => (confirmed ? HeadlinesStateActions.delete({ id }) : appActions.noop())));
      })
    )
  );

  delete$ = createEffect(() =>
    this.actions$.pipe(
      ofType(HeadlinesStateActions.delete),
      concatMap(({ id }) =>
        this.headlineService.deleteHeadline(id).pipe(
          map(_ => HeadlinesStateActions.deleteSuccess({ id })),
          catchError((error: unknown) => of(HeadlinesStateActions.deleteFailed({ error })))
        )
      )
    )
  );

  broadcastOnDeleteSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(HeadlinesStateActions.deleteSuccess),
      filter(_ => this.headlineService.shouldBroadcast),
      map(({ id }) =>
        HeadlinesStateActions.broadcastMessage({
          message: {
            messageType: HeadlineMessageType.delete,
            document: id,
            isCascadingMessage: false,
          },
        })
      )
    )
  );

  inMeetingRemoveDoneHeadline$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(HeadlinesStateActions.deleteSuccess),
        filter(_ => this.meetingService.currentMeeting?.inProgress),
        concatMap(({ id }) =>
          this.meetingService.removeDoneHeadline(id).pipe(
            map(() => HeadlinesStateActions.removeDoneHeadlineSuccess()),
            catchError(() => of(HeadlinesStateActions.removeDoneHeadlineFailure()))
          )
        )
      ),
    { dispatch: false }
  );

  //you can complete a CM in detail view, so we need to remove it from doneHeadlines as well
  //TODO NEXT: discuss feature
  inMeetingRemoveCascadedMessage$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(HeadlinesStateActions.deleteSuccess),
        filter(_ => this.meetingService.currentMeeting?.inProgress),
        filter(({ id }) => this.meetingService.currentMeeting?.cascadingMessages.includes(id)),
        concatMap(({ id }) =>
          this.meetingService.removeCascadingMessage(id).pipe(
            map(() => HeadlinesStateActions.removeCascadedMessageSuccess()),
            catchError(() => of(HeadlinesStateActions.removeCascadedMessageFailure()))
          )
        )
      ),
    { dispatch: false }
  );

  closeDetailIfOpened$ = createEffect(() =>
    this.actions$.pipe(
      ofType(HeadlinesStateActions.deleteLocal),
      concatLatestFrom(() => [this.store.select(headlinesSelectors.selectSelectedId)]),
      filter(([{ id }, selectedId]) => id === selectedId),
      map(_ => DetailViewActions.close())
    )
  );

  closeDetail$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        HeadlinesStateActions.delete,
        HeadlinesStateActions.setArchived,
        HeadlinesStateActions.clearSelected,
        HeadlinesStateActions.setTeam
      ),
      map(() => DetailViewActions.close())
    )
  );

  private isHeadlineAction(itemType: DetailType) {
    return itemType === DetailType.headline;
  }

  cascade$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(HeadlinesStateActions.cascade),
        switchMap(({ headline }) =>
          this.createDialogService.open({
            item: { ...headline, isCascadedMessage: true },
            itemType: ItemType.cascadedMessage,
          })
        )
      ),
    {
      dispatch: false,
    }
  );

  createIssue$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(HeadlinesStateActions.createIssue),
        concatMap(({ headline }) => {
          const createdFrom: FromLinkedItem = {
            id: headline._id,
            type: headline.isCascadedMessage ? LinkedItemTypeEnum.cascadingMessage : LinkedItemTypeEnum.headline,
          };
          return this.createDialogService.open({
            item: {
              title: headline.title,
              description: headline.description,
              comments: headline.comments,
              attachments: headline.attachments,
              userId: headline.ownedByUserId,
            },
            itemType: ItemType.issue,
            ...{ createdFrom },
          });
        })
      ),
    {
      dispatch: false,
    }
  );

  createTodo$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(HeadlinesStateActions.createTodo),
        concatMap(({ headline }) => {
          const createdFrom: FromLinkedItem = {
            id: headline._id,
            type: headline.isCascadedMessage ? LinkedItemTypeEnum.cascadingMessage : LinkedItemTypeEnum.headline,
          };
          return this.createDialogService.open({
            item: {
              title: headline.title,
              description: headline.description,
              comments: headline.comments,
              attachments: headline.attachments,
              userId: headline.ownedByUserId,
            },
            itemType: ItemType.todo,
            ...{ createdFrom },
          });
        })
      ),
    {
      dispatch: false,
    }
  );

  createRock$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(HeadlinesStateActions.createRock),
        concatMap(({ headline }) => {
          const createdFrom: FromLinkedItem = {
            id: headline._id,
            type: headline.isCascadedMessage ? LinkedItemTypeEnum.cascadingMessage : LinkedItemTypeEnum.headline,
          };
          return this.createDialogService.open({
            item: {
              title: headline.title,
              description: headline.description,
              comments: headline.comments,
              attachments: headline.attachments,
              userId: headline.ownedByUserId,
            },
            itemType: ItemType.rock,
            ...{ createdFrom },
          });
        })
      ),
    {
      dispatch: false,
    }
  );

  createHeadline$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(HeadlinesStateActions.createHeadline),
        concatMap(({ headline }) => {
          const createdFrom: FromLinkedItem = {
            id: headline._id,
            type: headline.isCascadedMessage ? LinkedItemTypeEnum.cascadingMessage : LinkedItemTypeEnum.headline,
          };
          return this.createDialogService.open({
            item: {
              title: headline.title,
              description: headline.description,
              comments: headline.comments,
              attachments: headline.attachments,
              userId: headline.ownedByUserId,
            },
            itemType: ItemType.headline,
            ...{ createdFrom },
          });
        })
      ),
    {
      dispatch: false,
    }
  );

  updateOrdinals$ = createEffect(() =>
    this.actions$.pipe(
      ofType(HeadlinesStateActions.updateOrdinals),
      concatLatestFrom(() => [
        this.store.select(headlinesSelectors.selectHeadlines),
        this.store.select(headlinesSelectors.selectPagination),
        this.store.select(headlinesSelectors.selectFilterBy),
        this.store.select(headlinesSelectors.selectSort),
      ]),
      map(([{ previousIndex, currentIndex }, headlines, pagination, filterBy, sort]) => {
        const start = Math.min(previousIndex, currentIndex) + pagination.index * pagination.size;
        const stop = Math.max(previousIndex, currentIndex) + pagination.index * pagination.size;

        const headlinesWithNewOrdinals = headlines.filter(t => t.ordinal >= start && t.ordinal <= stop);

        this.headlineService
          .updateOrdinals(
            headlinesWithNewOrdinals.map((h: Headline) => new OrdinalOrUserOrdinalUpdate(h._id, h.ordinal, 'ordinal')),
            'ordinal',
            filterBy.teamId,
            false,
            filterBy.inMeetingId,
            sort.field,
            sort.direction
          )
          .subscribe();

        return HeadlinesStateActions.clearSort();
      })
    )
  );

  print$ = createEffect(() =>
    this.actions$.pipe(
      ofType(HeadlinesStateActions.printHeadlinesAndCms),
      concatLatestFrom(() => [
        this.store.select(headlinesSelectors.selectFilterBy),
        this.store.select(headlinesSelectors.selectSort),
      ]),
      switchMap(([{ printOptions }, filterBy, sort]) =>
        this.printService
          .openPdf(PrintApi.headlinesAndCms, {
            teamId: filterBy.teamId,
            showArchived: filterBy.archived,
            searchText: filterBy.searchText,
            ...(sort.direction ? { sortField: sort.field, sortDirection: sort.direction } : null),
            printOptions,
          })
          .pipe(
            map(() => HeadlinesStateActions.printHeadlinesAndCmsSuccess()),
            catchError(() => of(HeadlinesStateActions.printHeadlinesAndCmsFailure()))
          )
      )
    )
  );

  routeTrashExcelExport$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FilterServiceActions.exportToExcel),
      filter(({ exportType }) => exportType === ExcelExportType.teamHeadlines),
      concatLatestFrom(() => this.store.select(headlinesSelectors.selectIsHeadlinesTrashView)),
      filter(([, isTrashView]) => isTrashView),
      map(() => HeadlinesStateActions.downloadDeletedHeadlinesExcel())
    )
  );

  downloadExcel$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FilterServiceActions.exportToExcel),
      filter(({ exportType }) => exportType === ExcelExportType.teamHeadlines),
      concatLatestFrom(() => [
        this.store.select(headlinesSelectors.selectIsHeadlinesTrashView),
        this.store.select(headlinesSelectors.selectFilterBy),
        this.store.select(headlinesSelectors.selectSortOrNull),
        this.store.select(cascadingMessagesSelectors.selectSortOrNull),
        this.filterService.selectedTeam$,
        this.store.select(selectLanguage),
      ]),
      filter(([, isTrashView]) => !isTrashView),
      switchMap(([, , filterBy, sortHeadlines, sortCascadingMessages, team, language]) => {
        let sortOptions = {
          ...(sortHeadlines ? { headlines: sortHeadlines } : null),
          ...(sortCascadingMessages ? { cascadingMessages: sortCascadingMessages } : null),
        };

        sortOptions = isEmpty(sortOptions) ? null : sortOptions;

        return this.headlineService
          .downloadExcel(filterBy, sortOptions)
          .pipe(
            tap(response => {
              const name = `${language.headline.items}_${language.cascadingMessage.items}_${team.name}`;
              const fileName = `${name}_${formatDate(new Date(), 'yyyy-MM-dd', 'en-US')}.xlsx`;
              this.fileService.downloadExcelFile(response, fileName);
            })
          )
          .pipe(
            map(() => HeadlinesStateActions.downloadExcelSuccess()),
            catchError(() => of(HeadlinesStateActions.downloadExcelFailure()))
          );
      })
    )
  );

  downloadDeletedHeadlinesExcel$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(HeadlinesStateActions.downloadDeletedHeadlinesExcel),
        concatLatestFrom(() => [
          this.store.select(TeamSelectors.selectFilterBarTeamId),
          this.store.select(FilterUserSelectors.selectFilterBarUserIds),
          this.store.select(selectLanguage),
          this.store.select(TeamSelectors.selectFilterBarTeam),
          this.filterService.searchText$,
        ]),
        filter(([, teamId]) => !!teamId),
        switchMap(([, teamId, filterBarUserIds, language, team, searchText]) => {
          const userIds =
            filterBarUserIds == null || filterBarUserIds.includes('all')
              ? []
              : filterBarUserIds.filter(userId => userId !== 'all');

          return this.headlineService.downloadDeletedHeadlinesExcel(teamId as string, userIds, searchText).pipe(
            tap(response => {
              const dateFormat = formatDate(new Date(), 'MMddyyyy', 'en-US');
              const itemsLabel = language?.headline?.items ?? 'Headlines';
              const fileName = `Deleted ${itemsLabel} ${team?.name ?? ''} ${dateFormat}.xlsx`;
              this.fileService.downloadExcelFile(response, fileName);
            }),
            catchError(() => EMPTY)
          );
        })
      ),
    { dispatch: false }
  );

  broadcastOnAttachmentsUpdated$ = createEffect(() =>
    this.actions$.pipe(
      ofType(HeadlinesStateActions.addAttachment, HeadlinesStateActions.removeAttachment),
      filter(_ => this.headlineService.shouldBroadcast),
      concatLatestFrom(({ id }) => [this.store.select(headlinesSelectors.selectHeadlineById(id))]),
      map(([_, headline]) =>
        HeadlinesStateActions.broadcastMessage({
          message: {
            messageType: HeadlineMessageType.headline,
            document: { attachments: headline?.attachments || [], _id: headline._id },
            isCascadingMessage: false,
          },
        })
      )
    )
  );

  archiveAllCompleted$ = createEffect(() =>
    this.actions$.pipe(
      ofType(HeadlinesStateActions.archiveAllCompleted),
      concatLatestFrom(() => [this.store.select(selectLanguage)]),
      exhaustMap(([_, language]) =>
        this.dialog
          .open<WarningConfirmDialogComponent, ConfirmDialogData>(WarningConfirmDialogComponent, {
            data: {
              title: 'Archive completed?',
              message: `All completed ${language.headline.items} and ${language.cascadingMessage.items} will be archived.`,
              confirmButtonText: 'Archive',
            },
          })
          .afterClosed()
          .pipe(
            filter(accepted => !!accepted),
            concatLatestFrom(_ => [this.store.select(headlinesSelectors.selectFilterByTeamId)]),
            exhaustMap(([, teamId]) =>
              this.headlineService.archiveAllCompleted(teamId).pipe(
                map(({ numOfArchivedHeadlines }) =>
                  HeadlinesStateActions.archiveAllCompletedSuccess({ numOfArchivedHeadlines })
                ),
                catchError((error: unknown) => of(HeadlinesStateActions.archiveAllCompletedFailure({ error })))
              )
            )
          )
      )
    )
  );

  notifyOnArchiveAllCompletedSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(HeadlinesStateActions.archiveAllCompletedSuccess),
      concatLatestFrom(() => [this.store.select(selectLanguage)]),
      map(([{ numOfArchivedHeadlines }, language]) =>
        NotificationActions.notify({
          message:
            numOfArchivedHeadlines > 0
              ? `Archived ${numOfArchivedHeadlines} ${language.headline.items} and/or ${language.cascadingMessage.items}.`
              : `No ${language.headline.items} or ${language.cascadingMessage.items} to archive.`,
          duration: 5000,
        })
      )
    )
  );

  broadcastMessage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(HeadlinesStateActions.broadcastMessage),
      filter(_ => this.headlineService.shouldBroadcast),
      map(({ message }) => {
        return RealTimeActions.sendMessage({
          channelId: this.headlineService.channelId,
          message: cloneDeep(message),
        });
      })
    )
  );

  handleRealTimeMessage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RealTimeActions.messageReceived),
      filter(({ channelId }) => channelId === this.headlineService.channelId),
      concatLatestFrom(() => [this.store.select(headlinesSelectors.selectInMeetingOnHeadlines)]),
      filter(([_, inMeetingOnHeadlines]) => inMeetingOnHeadlines),
      map(([{ message }]) => message as HeadlineReceivedRealtimeMessage),
      switchMap(message => {
        switch (message.messageType) {
          case HeadlineMessageType.new:
            if (!message.isCascadingMessage) {
              return of(HeadlinesStateActions.newHeadlineMessageReceived({ message }));
            }
          case HeadlineMessageType.delete:
            if (!message.isCascadingMessage) {
              return of(HeadlinesStateActions.deleteHeadlineMessageReceived({ message }));
            }
          case HeadlineMessageType.archive:
            if (!message.isCascadingMessage) {
              return of(HeadlinesStateActions.archiveHeadlineMessageReceived({ message }));
            }
          case HeadlineMessageType.unarchive:
            if (!message.isCascadingMessage) {
              return of(HeadlinesStateActions.unArchiveHeadlineMessageReceived({ message }));
            }
          case HeadlineMessageType.headline:
            if (!message.isCascadingMessage) {
              return of(HeadlinesStateActions.updateHeadlineMessageReceived({ message }));
            }
          case HeadlineMessageType.sort:
            if ((message.document as ListSortMessage).listType === 'headlines') {
              return of(HeadlinesStateActions.sortHeadlinesMessageReceived({ message }));
            }
          default:
            return EMPTY;
        }
      })
    )
  );

  handleNewHeadlineMessageReceived$ = createEffect(() =>
    this.actions$.pipe(
      ofType(HeadlinesStateActions.newHeadlineMessageReceived),
      switchMap(({ message }) => {
        return this.headlineService.getHeadlineById(message.document as string).pipe(
          map(headline => ({
            headline,
            selectedTeamId: extractValueFromStore(this.store, TeamSelectors.selectFilterBarTeamId),
          })),
          filter(({ headline, selectedTeamId }) => headline.teamId === selectedTeamId),
          map(({ headline }) => HeadlinesStateActions.addLocal({ headline })),
          catchError(() => of(HeadlinesStateActions.getFailed({ error: 'Error fetching headline' })))
        );
      })
    )
  );

  handleDeleteHeadlineMessageReceived$ = createEffect(() =>
    this.actions$.pipe(
      ofType(HeadlinesStateActions.deleteHeadlineMessageReceived),
      map(({ message }) => {
        return HeadlinesStateActions.deleteLocal({ id: message.document as string });
      })
    )
  );

  handleArchiveHeadlineMessageReceived$ = createEffect(() =>
    this.actions$.pipe(
      ofType(HeadlinesStateActions.archiveHeadlineMessageReceived),
      filter(_ => !extractValueFromStore(this.store, headlinesSelectors.selectFilterByArchived)),
      map(({ message }) => {
        return HeadlinesStateActions.deleteLocal({ id: message.document as string });
      })
    )
  );

  handleUnArchiveHeadlineMessageReceived$ = createEffect(() =>
    this.actions$.pipe(
      ofType(HeadlinesStateActions.unArchiveHeadlineMessageReceived),
      filter(_ => extractValueFromStore(this.store, headlinesSelectors.selectFilterByArchived)),
      map(({ message }) => {
        return HeadlinesStateActions.deleteLocal({ id: message.document as string });
      })
    )
  );

  handleUpdateHeadlineMessageReceived$ = createEffect(() =>
    this.actions$.pipe(
      ofType(HeadlinesStateActions.updateHeadlineMessageReceived),
      map(({ message }) => {
        const selectedTeamId = extractValueFromStore(this.store, TeamSelectors.selectFilterBarTeamId);
        const update = message.document as Partial<Headline>;
        if (update.teamId && update.teamId !== selectedTeamId) {
          return HeadlinesStateActions.deleteLocal({ id: update._id });
        } else {
          return HeadlinesStateActions.updateLocal({ update: { id: update._id, changes: update } });
        }
      })
    )
  );

  handleSortHeadlinesMessageReceived$ = createEffect(() =>
    this.actions$.pipe(
      ofType(HeadlinesStateActions.sortHeadlinesMessageReceived),
      concatLatestFrom(() => [
        this.store.select(selectCurrentUserId),
        this.store.select(MeetingsStateSelectors.selectCurrentMeetingPresenterUserId),
      ]),
      filter(([, currentUserId, presenterUserId]) => {
        // Skip sort change if current user is the presenter
        // just in case a race condition occurs and the presenter is not correctly set
        return currentUserId !== presenterUserId;
      }),
      map(([{ message }]) => {
        return HeadlinesStateActions.sortChange({
          sort: {
            field: (message.document as ListSortMessage).field as HeadlinesSortField,
            direction: (message.document as ListSortMessage).sortDirection,
          },
        });
      })
    )
  );

  startSpinner$ = createEffect(() =>
    this.actions$.pipe(
      ofType(HeadlinesStateActions.downloadExcel),
      map(() => SpinnerActions.startPrimary({ source: 'Headlines' }))
    )
  );

  stopSpinner$ = createEffect(() =>
    this.actions$.pipe(
      ofType(HeadlinesStateActions.downloadExcelSuccess, HeadlinesStateActions.downloadExcelFailure),
      map(() => SpinnerActions.stopPrimary({ source: 'Headlines' }))
    )
  );

  /** Reload the active list only when exiting trash (true → false), not on redundant setShowTrash(false). */
  setShowTrashReloadList$ = createEffect(() =>
    this.actions$.pipe(
      ofType(HeadlinesStateActions.setShowTrash),
      scan(
        (acc, { show }) => {
          const shouldReload = acc.previous && !show;
          return { previous: show, shouldReload };
        },
        { previous: false, shouldReload: false }
      ),
      filter(({ shouldReload }) => shouldReload),
      concatLatestFrom(() => this.store.select(headlinesSelectors.selectIsHeadlinesListPage)),
      filter(([, isListPage]) => isListPage),
      switchMap(() => of(HeadlinesStateActions.get(), CascadingMessagesStateActions.get()))
    )
  );

  setShowTrashOpenTrash$ = createEffect(() =>
    this.actions$.pipe(
      ofType(HeadlinesStateActions.setShowTrash),
      filter(({ show }) => show),
      map(() => DetailViewActions.close())
    )
  );

  getDeletedHeadlines$ = createEffect(() =>
    this.actions$.pipe(
      ofType(HeadlinesStateActions.getDeletedHeadlines),
      switchMap(({ teamId, pageIndex, pageSize, userIds, searchText, sort }) =>
        this.headlineService.getDeletedHeadlines({ teamId, pageIndex, pageSize, userIds, searchText, sort }).pipe(
          map(response =>
            HeadlinesStateActions.getDeletedHeadlinesSuccess({
              headlines: response.data,
              totalCount: response.totalCount,
            })
          ),
          catchError((error: unknown) => of(HeadlinesStateActions.getDeletedHeadlinesFailed({ error })))
        )
      )
    )
  );

  restoreHeadline$ = createEffect(() =>
    this.actions$.pipe(
      ofType(HeadlinesStateActions.restoreHeadline),
      concatMap(({ headlineId }) =>
        this.headlineService.restoreHeadline(headlineId).pipe(
          map(headline => HeadlinesStateActions.restoreHeadlineSuccess({ headlineId, headline })),
          catchError((error: unknown) => of(HeadlinesStateActions.restoreHeadlineFailed({ error })))
        )
      )
    )
  );

  restoreHeadlineSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(HeadlinesStateActions.restoreHeadlineSuccess),
      concatLatestFrom(() => [this.store.select(selectLanguage)]),
      switchMap(([{ headline }, language]) => {
        const itemLabel = headline?.isCascadedMessage
          ? (language?.cascadingMessage?.item ?? 'Cascading Message')
          : (language?.headline?.item ?? 'Headline');

        return of(
          NotificationActions.notify({
            message: `${itemLabel} and associated content have been restored.`,
            duration: 5000,
          }),
          DetailViewActions.close()
        );
      })
    )
  );

  openDeleteForeverHeadlineConfirmation$ = createEffect(() =>
    this.actions$.pipe(
      ofType(HeadlinesStateActions.openDeleteForeverHeadlineConfirmation),
      concatLatestFrom(() => [this.store.select(selectLanguage)]),
      exhaustMap(([{ headline }, language]) => {
        const headlineId = headline._id;
        if (!headlineId) return EMPTY;

        const itemLabel = headline.isCascadedMessage
          ? (language?.cascadingMessage?.item ?? 'Cascading Message')
          : (language?.headline?.item ?? 'Headline');

        return this.dialog
          .open<WarningConfirmDialogComponent, ConfirmDialogData>(WarningConfirmDialogComponent, {
            autoFocus: false,
            disableClose: true,
            width: '450px',
            maxWidth: 450,
            data: {
              title: `Are you sure you want to delete this ${itemLabel}?`,
              message: `This will permanently remove the ${itemLabel} and all its associated content. This action cannot be undone.`,
              confirmButtonText: 'Delete Forever',
            },
          })
          .afterClosed()
          .pipe(
            filter(confirmed => !!confirmed),
            map(() =>
              HeadlinesStateActions.permanentDeleteHeadline({
                headlineId,
                isCascadedMessage: headline.isCascadedMessage,
              })
            )
          );
      })
    )
  );

  permanentDeleteHeadline$ = createEffect(() =>
    this.actions$.pipe(
      ofType(HeadlinesStateActions.permanentDeleteHeadline),
      concatMap(({ headlineId, isCascadedMessage }) =>
        this.headlineService.permanentlyDeleteHeadline(headlineId).pipe(
          map(() => HeadlinesStateActions.permanentDeleteHeadlineSuccess({ headlineId, isCascadedMessage })),
          catchError((error: unknown) => of(HeadlinesStateActions.permanentDeleteHeadlineFailed({ error })))
        )
      )
    )
  );

  permanentDeleteHeadlineSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(HeadlinesStateActions.permanentDeleteHeadlineSuccess),
      concatLatestFrom(() => [this.store.select(selectLanguage)]),
      switchMap(([{ isCascadedMessage }, language]) => {
        const itemLabel = isCascadedMessage
          ? (language?.cascadingMessage?.item ?? 'Cascading Message')
          : (language?.headline?.item ?? 'Headline');

        return of(
          NotificationActions.notify({
            message: `${itemLabel} permanently deleted.`,
            duration: 5000,
          }),
          DetailViewActions.close()
        );
      })
    )
  );
}
